import { useId } from 'react'
import {
  CLAIM_DAMAGE_SECTION_INTRO,
  CLAIM_DESCRIBE_MAX_FIELD_LENGTH,
  MULTI_VEHICLE_DAMAGE_PROMPTS,
  OTHER_BRANCH_PARKED_INCIDENT_PROMPTS,
  OTHER_BRANCH_WHAT_HAPPENED_INTRO,
} from '../claims/claimDescribeCopy'
import {
  claimFormFieldClass,
  ClaimFormFieldError,
  ClaimFormSubmitSummaryAlert,
} from '../claims/claimFormValidationFeedback'

const DEFAULT_TEXTAREA_CLASS =
  'min-h-[168px] w-full resize-y rounded-xl border border-solid border-border-primary bg-background-primary px-[16px] py-[14px] font-body text-sm leading-sm text-text-primary-900 shadow-xs placeholder:text-text-placeholder focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground-brand-solid focus-visible:ring-offset-2'

/** Props for the shared incident + damage two-field describe form. */
export interface ClaimIncidentDamageDescribeFormProps {
  incident: string
  damage: string
  onIncidentChange: (value: string) => void
  onDamageChange: (value: string) => void
  fieldErrors: { incident: boolean; damage: boolean }
  showSubmitSummary: boolean
  clearSubmitAttempt: () => void
  maxLength?: number
  textareaClassName?: string
  /** When true, only the incident (“What happened”) section is shown (e.g. whole vehicle stolen). */
  hideDamageSection?: boolean
}

/**
 * Two-field incident + damage describe layout (matches multi-vehicle collision structure).
 */
export default function ClaimIncidentDamageDescribeForm({
  incident,
  damage,
  onIncidentChange,
  onDamageChange,
  fieldErrors,
  showSubmitSummary,
  clearSubmitAttempt,
  maxLength = CLAIM_DESCRIBE_MAX_FIELD_LENGTH,
  textareaClassName = DEFAULT_TEXTAREA_CLASS,
  hideDamageSection = false,
}: ClaimIncidentDamageDescribeFormProps) {
  const incidentFieldId = useId()
  const damageFieldId = useId()

  return (
    <section className="flex w-full flex-col items-start gap-[24px]">
      {showSubmitSummary ? <ClaimFormSubmitSummaryAlert /> : null}
      <div className="flex w-full flex-col items-start gap-[12px]">
        <h2 className="m-0 font-body font-semibold text-md leading-md text-text-primary-900">
          What happened
        </h2>
        <p className="m-0 font-body text-sm leading-sm text-text-primary-900">
          {OTHER_BRANCH_WHAT_HAPPENED_INTRO}
        </p>
        <p className="m-0 font-body text-sm font-semibold leading-sm text-text-primary-900">Include things like:</p>
        <ul className="m-0 flex list-disc flex-col gap-[8px] pl-[20px] font-body text-sm leading-sm text-text-secondary-700 marker:text-text-secondary-700">
          {OTHER_BRANCH_PARKED_INCIDENT_PROMPTS.map((item) => (
            <li key={item} className="pl-[4px]">
              {item}
            </li>
          ))}
        </ul>
        <div className="flex w-full flex-col items-stretch gap-[8px]">
          <label htmlFor={incidentFieldId} className="sr-only">
            Incident description (maximum {maxLength} characters)
          </label>
          <textarea
            id={incidentFieldId}
            value={incident}
            maxLength={maxLength}
            onChange={(e) => {
              clearSubmitAttempt()
              onIncidentChange(e.target.value)
            }}
            placeholder="Describe what happened leading up to and during the incident…"
            rows={7}
            aria-invalid={fieldErrors.incident}
            aria-describedby={fieldErrors.incident ? `${incidentFieldId}-error` : undefined}
            className={claimFormFieldClass(textareaClassName, fieldErrors.incident)}
          />
          {fieldErrors.incident ? (
            <ClaimFormFieldError id={`${incidentFieldId}-error`}>
              Describe what happened to continue.
            </ClaimFormFieldError>
          ) : null}
          <p className="m-0 self-end font-body text-xs leading-xs text-text-tertiary-600" aria-live="polite">
            {incident.length}/{maxLength} characters
          </p>
        </div>
      </div>

      {hideDamageSection ? null : (
        <>
      <div className="h-px w-full bg-border-secondary" aria-hidden />

      <div className="flex w-full flex-col items-start gap-[12px]">
        <h2 className="m-0 font-body font-semibold text-md leading-md text-text-primary-900">Damage and impact</h2>
        <p className="m-0 font-body text-sm leading-sm text-text-primary-900">{CLAIM_DAMAGE_SECTION_INTRO}</p>
        <p className="m-0 font-body text-sm font-semibold leading-sm text-text-primary-900">Consider including:</p>
        <ul className="m-0 flex list-disc flex-col gap-[8px] pl-[20px] font-body text-sm leading-sm text-text-secondary-700 marker:text-text-secondary-700">
          {MULTI_VEHICLE_DAMAGE_PROMPTS.map((item) => (
            <li key={item} className="pl-[4px]">
              {item}
            </li>
          ))}
        </ul>
        <div className="flex w-full flex-col items-stretch gap-[8px]">
          <label htmlFor={damageFieldId} className="sr-only">
            Damage details (maximum {maxLength} characters)
          </label>
          <textarea
            id={damageFieldId}
            value={damage}
            maxLength={maxLength}
            onChange={(e) => {
              clearSubmitAttempt()
              onDamageChange(e.target.value)
            }}
            placeholder="Describe damage, contact points, drivability, and other property…"
            rows={7}
            aria-invalid={fieldErrors.damage}
            aria-describedby={fieldErrors.damage ? `${damageFieldId}-error` : undefined}
            className={claimFormFieldClass(textareaClassName, fieldErrors.damage)}
          />
          {fieldErrors.damage ? (
            <ClaimFormFieldError id={`${damageFieldId}-error`}>Describe the damage to continue.</ClaimFormFieldError>
          ) : null}
          <p className="m-0 self-end font-body text-xs leading-xs text-text-tertiary-600" aria-live="polite">
            {damage.length}/{maxLength} characters
          </p>
        </div>
      </div>
        </>
      )}
    </section>
  )
}
